import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { Dispatch, RootState } from '../../store/store';
import Modal from '../Modal/Modal';
import { DEFAULT_INPUT_CLASS } from '../InputControl/constants';

type OwnProps = {
  isVisible: boolean;
  onClose: () => void;
  controllerId: string;
};

type ComponentProps = OwnProps & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

// Same mandatory yes/no pattern as ApplyControllerSetupModal - see that file
// for why these can't default to unchecked.
type YesNo = 'yes' | 'no' | null;

const ApplySetupToControllerModal: React.FC<ComponentProps> = ({
  isVisible,
  onClose,
  controllerId,
  controllerSetups,
  getControllerSetups,
  applyControllerSetup,
}) => {
  const [selectedSetupId, setSelectedSetupId] = useState<string>('');
  const [overwriteChannelNames, setOverwriteChannelNames] = useState<YesNo>(null);
  const [overwriteCurrentScaling, setOverwriteCurrentScaling] = useState<YesNo>(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    if (isVisible) {
      getControllerSetups();
    }
  }, [isVisible]);

  const resetAndClose = () => {
    setSelectedSetupId('');
    setOverwriteChannelNames(null);
    setOverwriteCurrentScaling(null);
    onClose();
  };

  const canSubmit =
    !isApplying && !!selectedSetupId && overwriteChannelNames !== null && overwriteCurrentScaling !== null;

  const onApplyHandler = async () => {
    if (!canSubmit) return;

    setIsApplying(true);
    const results = await applyControllerSetup({
      id: selectedSetupId,
      payload: {
        controllerIds: [controllerId],
        overwriteChannelNames: overwriteChannelNames === 'yes',
        overwriteCurrentScaling: overwriteCurrentScaling === 'yes',
      },
    });
    setIsApplying(false);

    if (!results) return;

    const [result] = results;
    if (result?.success) {
      toast.success('Controller setup applied');
    } else {
      toast.error(result?.error || 'Failed to apply controller setup');
    }

    resetAndClose();
  };

  return (
    <Modal
      isVisible={isVisible}
      modalTitle="Apply Controller Setup"
      onClose={resetAndClose}
      onAccept={onApplyHandler}
      acceptBtnText={isApplying ? 'Applying...' : 'Apply'}
      acceptBtnDisabled={!canSubmit}
      cancelBtnDisabled={isApplying}
    >
      <div className="flex w-full flex-col gap-4 text-left">
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">Select a controller setup</p>
          <select
            className={DEFAULT_INPUT_CLASS}
            value={selectedSetupId}
            onChange={(event) => setSelectedSetupId(event.target.value)}
          >
            <option value="" disabled>
              Select a controller setup...
            </option>
            {(controllerSetups ?? []).map((setup) => (
              <option key={setup.id} value={setup.id}>
                {setup.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">
            Overwrite the channel names already on this controller?
          </p>
          <div className="flex gap-5">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteChannelNames"
                checked={overwriteChannelNames === 'yes'}
                onChange={() => setOverwriteChannelNames('yes')}
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteChannelNames"
                checked={overwriteChannelNames === 'no'}
                onChange={() => setOverwriteChannelNames('no')}
              />
              No
            </label>
          </div>
        </div>
        <div>
          <p className="mb-2 font-medium text-black dark:text-white">
            Overwrite the current scaling settings already on this controller?
          </p>
          <div className="flex gap-5">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteCurrentScaling"
                checked={overwriteCurrentScaling === 'yes'}
                onChange={() => setOverwriteCurrentScaling('yes')}
              />
              Yes
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="overwriteCurrentScaling"
                checked={overwriteCurrentScaling === 'no'}
                onChange={() => setOverwriteCurrentScaling('no')}
              />
              No
            </label>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const mapState = (state: RootState) => ({
  controllerSetups: state.controller_setups.controllerSetups,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllerSetups: dispatch.controller_setups.getControllerSetups,
  applyControllerSetup: dispatch.controller_setups.applyControllerSetup,
});

export default connect(mapState, mapDispatch)(ApplySetupToControllerModal);
